import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronDown, ChevronUp, Compass } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";

export default function LrfFrameworkPage() {
    const [framework, setFramework] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeKey, setActiveKey] = useState("vision");
    const [expandedObj, setExpandedObj] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/lrf/framework");
                setFramework(data.domains);
            } catch (e) {
                toast.error(extractError(e, "Could not load framework"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="text-[#0B2545] inline-flex items-center gap-2" data-testid="framework-loading">
                <Loader2 className="animate-spin" size={16} /> Loading framework…
            </div>
        );
    }
    if (!framework) return null;

    const active = framework.find((d) => d.key === activeKey) || framework[0];

    return (
        <div className="space-y-6" data-testid="lrf-framework">
            <div>
                <Link to="/app/lrf" className="text-xs text-[#1E56A0] hover:underline">← Back to Reflections</Link>
                <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545]">
                    LRF · Framework
                </h1>
                <p className="mt-2 text-sm text-[#0E1B2C]/70">
                    Explore the full framework — 6 domains × 6 objectives × 6 reflection questions.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <aside className="lg:col-span-1 rounded-2xl bg-white border border-[#D6E6F2] p-2" data-testid="framework-domain-nav">
                    {framework.map((d) => {
                        const selected = d.key === activeKey;
                        return (
                            <button
                                key={d.key}
                                onClick={() => setActiveKey(d.key)}
                                data-testid={`framework-tab-${d.key}`}
                                className={`w-full text-left rounded-xl px-3 py-2 mb-1 transition-colors flex items-center gap-2 ${
                                    selected ? "bg-[#0B2545] text-white" : "hover:bg-[#F1F6FB] text-[#0B2545]"
                                }`}
                            >
                                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: d.color_hex }} />
                                <span className="text-sm font-semibold truncate">
                                    {d.position}. {d.name}
                                </span>
                            </button>
                        );
                    })}
                </aside>

                <div className="lg:col-span-3 rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid={`framework-detail-${active.key}`}>
                    <div className="h-1.5 -mx-5 -mt-5 mb-4 rounded-t-2xl" style={{ backgroundColor: active.color_hex }} />
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold" style={{ color: active.color_hex }}>
                        Domain {active.position} · {active.objectives.length} objectives
                    </div>
                    <h2 className="mt-1 font-serif text-2xl font-semibold text-[#0B2545]">
                        {active.name}
                    </h2>

                    <div className="mt-5 space-y-3">
                        {active.objectives.map((o) => {
                            const key = `${active.key}:${o.id}`;
                            const open = expandedObj[key];
                            return (
                                <div key={o.id} className="rounded-xl border border-[#D6E6F2] overflow-hidden" data-testid={`obj-${active.key}-${o.position}`}>
                                    <button
                                        onClick={() => setExpandedObj((prev) => ({ ...prev, [key]: !open }))}
                                        className="w-full flex items-start justify-between gap-2 px-4 py-3 text-left hover:bg-[#F1F6FB]"
                                    >
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold">
                                                Objective {o.position}
                                            </div>
                                            <div className="font-serif text-lg text-[#0B2545] font-semibold mt-0.5">
                                                {o.name}
                                            </div>
                                        </div>
                                        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {open && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="px-4 pb-4 space-y-3">
                                                    <p className="text-sm text-[#0E1B2C]/80 italic">
                                                        {o.purpose}
                                                    </p>
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold mb-1">Look-Fors ({o.look_fors.length})</div>
                                                        <ul className="list-disc pl-5 space-y-1 text-sm text-[#0E1B2C]">
                                                            {o.look_fors.map((lf) => <li key={lf.id}>{lf.text}</li>)}
                                                        </ul>
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold mb-1">Reflection Questions ({o.questions.length})</div>
                                                        <ol className="list-decimal pl-5 space-y-1 text-sm text-[#0E1B2C]">
                                                            {o.questions.map((q) => <li key={q.id}>{q.text}</li>)}
                                                        </ol>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
